import React from "react";
import { Box, Typography } from "@mui/joy";
import { Check } from "lucide-react";
import { useResponsive } from "../hooks";

type Step = {
    label?: string;
    Name?: string;
};

type ProgressStepperProps = {
    steps: Step[];
    currentStep: number; // 0-based index
    onClick: any;
    radius?: number;
};

const stepperHeight = 37;

const ProgressStepper: React.FC<ProgressStepperProps> = ({
    steps,
    currentStep,
    onClick,
    radius = 30,
}) => {
    const { isMobile, isTablet } = useResponsive();
    const isCompact = isMobile || isTablet;

    if (!steps || steps.length === 0) return null;

    if (isCompact) {
        return (
            <CompactStepper
                steps={steps}
                currentStep={currentStep}
                onClick={onClick}
            />
        );
    }

    return (
        <DesktopStepper
            steps={steps}
            currentStep={currentStep}
            onClick={onClick}
            radius={radius}
        />
    );
};

/* ─── Mobile: Compact dots with active label ─── */
function CompactStepper({
    steps,
    currentStep,
    onClick,
}: {
    steps: Step[];
    currentStep: number;
    onClick: any;
}) {
    const activeLabel =
        steps[currentStep]?.label || steps[currentStep]?.Name || "";

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                width: "100%",
                py: 0.5,
                minWidth: 0,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    flex: 1,
                    minWidth: 0,
                    overflowX: "auto",
                    px: 0.75,
                    py: 0.75,
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                }}
            >
                {steps.map((step, index) => {
                    const isCompleted = index < currentStep;
                    const isCurrent = index === currentStep;
                    const dotSize = isCurrent ? 28 : 22;

                    return (
                        <React.Fragment key={step.label || step.Name}>
                            <Box
                                onClick={() => onClick(step)}
                                sx={{
                                    width: dotSize,
                                    height: dotSize,
                                    minWidth: dotSize,
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    flexShrink: 0,
                                    transition:
                                        "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                                    bgcolor: isCompleted
                                        ? "success.solidBg"
                                        : isCurrent
                                          ? "primary.solidBg"
                                          : "background.level2",
                                    border:
                                        isCompleted || isCurrent
                                            ? "none"
                                            : "2px solid",
                                    borderColor: "neutral.outlinedBorder",
                                    boxShadow: isCurrent
                                        ? "0 0 0 3px rgba(var(--standard-palette-primary-mainChannel) / 0.2)"
                                        : "none",
                                    "&:active": { transform: "scale(0.9)" },
                                }}
                            >
                                {isCompleted ? (
                                    <Check
                                        size={12}
                                        color="white"
                                        strokeWidth={3}
                                    />
                                ) : (
                                    <Typography
                                        sx={{
                                            fontSize: isCurrent ? "11px" : "10px",
                                            fontWeight: 700,
                                            color: isCurrent
                                                ? "primary.solidColor"
                                                : "text.tertiary",
                                            lineHeight: 1,
                                        }}
                                    >
                                        {index + 1}
                                    </Typography>
                                )}
                            </Box>

                            {index < steps.length - 1 && (
                                <Box
                                    sx={{
                                        flex: 1,
                                        height: "2px",
                                        minWidth: 8,
                                        borderRadius: "1px",
                                        bgcolor:
                                            index < currentStep
                                                ? "success.solidBg"
                                                : "neutral.outlinedBorder",
                                        transition: "background-color 0.3s ease",
                                    }}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
            </Box>

            {/* Active step label — fixed, outside the overflow area */}
            <Box
                sx={{
                    ml: 1,
                    pl: 1,
                    borderLeft: "2px solid",
                    borderColor: "primary.300",
                    flexShrink: 0,
                    maxWidth: "40%",
                }}
            >
                <Typography
                    level="body-xs"
                    sx={{
                        color: "text.tertiary",
                        fontWeight: 600,
                        fontSize: "9px",
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        lineHeight: 1,
                    }}
                >
                    Step {currentStep + 1}/{steps.length}
                </Typography>
                <Typography
                    level="body-sm"
                    sx={{
                        fontWeight: 700,
                        color: "text.primary",
                        fontSize: "12px",
                        lineHeight: 1.2,
                        mt: 0.25,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                >
                    {activeLabel}
                </Typography>
            </Box>
        </Box>
    );
}

/* ─── Desktop: Original chevron stepper ─── */
function DesktopStepper({
    steps,
    currentStep,
    onClick,
    radius,
}: {
    steps: Step[];
    currentStep: number;
    onClick: any;
    radius: number;
}) {
    return (
        <Box
            sx={{
                display: "flex",
                width: "100%",
                minWidth: 0,
                backgroundColor: "background.surface",
                borderRadius: `${radius}px`,
                overflow: "hidden",
                gap: 2,
            }}
        >
            {steps.map((step, index) => {
                const isCompleted = index < currentStep;
                const isCurrent = index === currentStep;

                return (
                    <Box
                        key={step.label || step.Name}
                        onClick={() => onClick(step)}
                        sx={(palette: any) => ({
                            flex: 1,
                            minWidth: 0,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative",
                            backgroundColor: isCompleted
                                ? "success.solidBg"
                                : isCurrent
                                  ? "primary.solidBg"
                                  : "background.level2",
                            height: stepperHeight,
                            "&::after": {
                                content: '""',
                                position: "absolute",
                                bottom: "0",
                                left: "-.25rem",
                                right: "-.3125rem",
                                backgroundColor: isCompleted
                                    ? "success.solidBg"
                                    : isCurrent
                                      ? "primary.solidBg"
                                      : "background.level2",
                                height: stepperHeight / 2,
                                transform: "skew(-30deg) translate3d(0, 0, 0)",
                            },
                            "&::before": {
                                content: '""',
                                position: "absolute",
                                top: "0",
                                left: "-.25rem",
                                right: "-.3125rem",
                                backgroundColor: isCompleted
                                    ? "success.solidBg"
                                    : isCurrent
                                      ? "primary.solidBg"
                                      : "background.level2",
                                height: stepperHeight / 2,
                                transform: "skew(28deg) translate3d(0, 0, 0)",
                            },
                            border: "none",
                        })}
                    >
                        <Typography
                            level="body-sm"
                            sx={{
                                zIndex: 1002,
                                fontWeight: 500,
                                color: isCurrent
                                    ? "primary.solidColor"
                                    : isCompleted
                                      ? "primary.solidColor"
                                      : "text.secondary",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: "80%",
                                textAlign: "center",
                            }}
                        >
                            {step.label || step.Name}
                        </Typography>
                    </Box>
                );
            })}
        </Box>
    );
}

export default ProgressStepper;
